import invariant from 'invariant'
import * as React from 'react'

import { styled } from '~/styled'
import { NavigatorActions } from '~/types/navigatorActions.types'
import type { TOptionalTooltipWrapperProps } from '~/utilities/optional-tooltip-wrapper/OptionalTooltipWrapper'
import { OptionalTooltipWrapper } from '~/utilities/optional-tooltip-wrapper/OptionalTooltipWrapper'
import { Override } from '~/utilities/types'
import { getExternalAnchorProps } from '~/utilities/uri'

import { Icon } from '../icon/Icon'
import { ActionIconSizeMap } from './ActionIcon.constants'

const StyledButton = styled(
  'button',
  {
    base: [
      'items-center',
      'appearance-none',
      'bg-white',
      'rounded-md',
      'box-border',
      'cursor-pointer',
      'flex',
      'shrink-0',
      'justify-center',
      'transition-all',
      'duration-100',
      'ease-out',
      'disabled:cursor-not-allowed',
      'disabled:opacity-30'
    ],
    variants: {
      theme: {
        neutral: [
          '[--solid-base:var(--color-white)]',
          '[--solid-interact:var(--color-grey-100)]',
          '[--solid-active:var(--color-grey-200)]',
          '[--solid-text:var(--color-grey-1200)]',
          '[--base:var(--color-grey-700)]',
          '[--interact:var(--color-primary-900)]',
          '[--active:var(--color-primary-1100)]'
        ],
        primary: [
          '[--base:var(--color-primary-800)]',
          '[--interact:var(--color-primary-900)]',
          '[--active:var(--color-primary-1000)]'
        ],
        primaryDark: [
          '[--base:var(--color-primary-1000)]',
          '[--interact:var(--color-primary-1100)]',
          '[--active:var(--color-primary-1200)]'
        ],
        success: [
          '[--base:var(--color-success)]',
          '[--interact:var(--color-success-mid)]',
          '[--active:var(--color-success-dark)]'
        ],
        warning: [
          '[--base:var(--color-warning)]',
          '[--interact:var(--color-warning-mid)]',
          '[--active:var(--color-warning-dark)]'
        ],
        danger: [
          '[--base:var(--color-danger)]',
          '[--interact:var(--color-danger-mid)]',
          '[--active:var(--color-danger-dark)]'
        ],
        white: [
          '[--base:var(--color-white)]',
          '[--interact:var(--color-white)]',
          '[--active:var(--color-white)]'
        ]
      },
      appearance: {
        simple: [
          'bg-transparent',
          'text-(--base)',
          'not-disabled:hover:text-(--interact)',
          'not-disabled:focus:text-(--interact)',
          'not-disabled:active:text-(--active)'
        ],
        outline: [
          'bg-[unset]',
          'border',
          'border-current',
          'text-(--base)',
          'not-disabled:hover:text-(--interact)',
          'not-disabled:focus:text-(--interact)',
          'not-disabled:active:text-(--active)'
        ],
        solid: [
          'bg-(--solid-base,var(--base))',
          'text-(--solid-text,white)',
          'not-disabled:hover:bg-(--solid-interact,var(--interact))',
          'not-disabled:hover:text-(--solid-text,white)',
          'not-disabled:focus:bg-(--solid-interact,var(--interact))',
          'not-disabled:focus:text-(--solid-text,white)',
          'not-disabled:active:bg-(--solid-active,var(--active))'
        ]
      },
      size: {
        xs: ['size-6'],
        sm: ['size-8'],
        md: ['size-10'],
        lg: ['size-12']
      },
      isRounded: {
        true: ['rounded-full']
      }
    }
  },
  {
    enabledResponsiveVariants: true
  }
)

type ActionIconProps = Override<
  React.ComponentProps<typeof StyledButton>,
  {
    children: React.ReactNode
    label: string
  } & Omit<TOptionalTooltipWrapperProps, 'label'> &
    NavigatorActions
>

export const ActionIcon = React.forwardRef<HTMLButtonElement, ActionIconProps>(
  (
    {
      children,
      theme = 'primary',
      appearance = 'simple',
      size = 'sm',
      label,
      href,
      disabled,
      hasTooltip = true,
      tooltipSide,
      ...remainingProps
    },
    ref
  ) => {
    const INVALID_CHILDREN_MESSAGE = `A single ${Icon.displayName} component is permitted as a child of ${ActionIcon.displayName}`

    invariant(React.Children.count(children) === 1, INVALID_CHILDREN_MESSAGE)

    // Polymorphic props: can be either button or anchor element
    // Type assertion needed due to TypeScript limitations with polymorphic components
    const optionalLinkProps = href
      ? ({
          as: 'a',
          href: disabled ? undefined : href,
          onClick: undefined,
          'aria-disabled': !!disabled
        } as const)
      : ({ type: 'button' } as const)

    return (
      <OptionalTooltipWrapper
        hasTooltip={hasTooltip}
        label={label}
        tooltipSide={tooltipSide}
      >
        <StyledButton
          {...(optionalLinkProps as any)}
          {...getExternalAnchorProps(href)}
          {...remainingProps}
          aria-label={label}
          theme={theme}
          appearance={appearance}
          size={size}
          ref={ref as any}
          disabled={disabled}
        >
          {React.Children.map(children, (child) => {
            // TS needs this check for any following code to access child.type
            // even with optional chaining
            if (!React.isValidElement(child)) {
              throw new Error(INVALID_CHILDREN_MESSAGE)
            }

            invariant(
              child.type === Icon,
              `Children of type ${child?.type} aren't permitted. Only an ${Icon.displayName} component is allowed in ${ActionIcon.displayName}`
            )

            return React.cloneElement(
              child as React.ReactElement<React.ComponentProps<typeof Icon>>,
              {
                size: ActionIconSizeMap[size as string]
              }
            )
          })}
        </StyledButton>
      </OptionalTooltipWrapper>
    )
  }
)

ActionIcon.displayName = 'ActionIcon'
